import { useEffect, useRef } from 'react';

/**
 * What a modal dialog owes the keyboard (WAI-ARIA APG, dialog pattern; UX audit 17/10/2026):
 * focus moves into it when it opens (to `[data-autofocus]`, else its first field or button,
 * else the dialog itself), Tab and Shift+Tab go round inside it, Esc closes it, and closing
 * gives focus back to what opened it. Dialogs stack: only the top one answers the keys, so a
 * confirmation over a dialog closes alone.
 *
 * An Esc already handled inside (an inline edit cancelled, a menu closed) calls
 * preventDefault and is left alone.
 */
const stack: HTMLElement[] = [];

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

function focusables(root: HTMLElement): HTMLElement[] {
  return [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((el) => el.offsetParent !== null || el === document.activeElement);
}

export function useModal<T extends HTMLElement>(onClose?: () => void, open = true) {
  const ref = useRef<T>(null);
  const close = useRef(onClose);
  close.current = onClose;

  useEffect(() => {
    const el = ref.current;
    if (!open || !el) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    stack.push(el);
    if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');

    // After the first paint, so fields rendered with the dialog exist.
    const raf = requestAnimationFrame(() => {
      if (el.contains(document.activeElement)) return;
      const first = el.querySelector<HTMLElement>('[data-autofocus]') ?? focusables(el)[0];
      (first ?? el).focus({ preventScroll: true });
    });

    const onKey = (e: KeyboardEvent) => {
      if (stack[stack.length - 1] !== el) return;
      if (e.key === 'Escape') {
        if (e.defaultPrevented || !close.current) return;
        e.preventDefault();
        close.current();
        return;
      }
      if (e.key !== 'Tab') return;
      const items = focusables(el);
      if (items.length === 0) {
        e.preventDefault();
        el.focus();
        return;
      }
      const first = items[0]!;
      const last = items[items.length - 1]!;
      const active = document.activeElement;
      if (!el.contains(active)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
      } else if (e.shiftKey && (active === first || active === el)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    // Focus that wanders out (a click on the page behind) is brought back on the next Tab.
    document.addEventListener('keydown', onKey);

    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('keydown', onKey);
      const i = stack.lastIndexOf(el);
      if (i >= 0) stack.splice(i, 1);
      if (opener && opener.isConnected && (!document.activeElement || document.activeElement === document.body || el.contains(document.activeElement) || !document.activeElement.isConnected)) {
        opener.focus({ preventScroll: true });
      }
    };
  }, [open]);

  return ref;
}
